import React, { useState, useEffect } from "react";
import {
  Grid,
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Checkbox,
  Link,
  CircularProgress,
} from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { useNavigate } from "react-router-dom";
import CheckOutCommon from "./CheckOutCommon";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AddNewCardModal from "./AddNewCardModal";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { CustomTextField } from "components";
import { useAuth } from "context/authContext";
import { CartService } from "services/CartService";
import { useLocation } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  useStripe,
  useElements,
  CardElement,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
// import axios from "axios";
import { PurchasesService } from "services/PurchasesService";
import { validationStripeSchema } from "schema";
import { Field, Form, Formik } from "formik";
import { createPaymentIntent } from "../../../../utils/firebaseFunctions";
import { Cart, PaymentMethodsTypes } from "types";
import { PaymentMethodService } from "services/PaymentMethodService";
import { and, Timestamp } from "firebase/firestore";

type CardStripe = {
  cardNumber: string;
  expirationDate: string | undefined;
  cvc: string;
  countrySelected: string;
};

// interface CreatePaymentIntentResponse {
//   clientSecret: string;
// }

// Update the STRIPE_ELEMENT_STYLES constant
const STRIPE_ELEMENT_STYLES = {
  base: {
    fontSize: "16px",
    color: "#424770",
    fontFamily: "Arial, sans-serif",
    "::placeholder": {
      color: "#aab7c4",
    },
    iconColor: "#666EE8",
  },
  invalid: {
    color: "#9e2146",
    ":focus": {
      color: "#9e2146",
    },
  },
};

// Update the options objects
const CARD_NUMBER_ELEMENT_OPTIONS = {
  style: STRIPE_ELEMENT_STYLES,
  showIcon: true,
  placeholder: "1234 1234 1234 1234",
};

const CARD_EXPIRY_ELEMENT_OPTIONS = {
  style: STRIPE_ELEMENT_STYLES,
  placeholder: "MM/YY",
};

const CARD_CVC_ELEMENT_OPTIONS = {
  style: STRIPE_ELEMENT_STYLES,
  placeholder: "CVC",
};

const CheckOutData = () => {
  const stripe = useStripe();
  const elements = useElements();
  const { currentUser, handleUpdateCartItem, isLoggedIn } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();
  const { data } = location.state || { data: [] };
  const [subtotal, setSubtotal] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [totalDue, setTotalDue] = useState(0);
  const [tax, setTax] = useState(0);
  const [open, setOpen] = useState(false);
  const [expirationDate, setExpirationDate] = useState<Dayjs | null>(dayjs());
  const [cvc, setCvc] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [countrySelected, setCountrySelected] = useState("");
  const [isSaveCardChecked, setIsSaveCardChecked] = useState(false);
  const [cards, setCards] = useState<PaymentMethodsTypes[]>([]);
  const [showModal, setShowModal] = useState(cards.length === 0);
  const [loading, setLoading] = useState(false);

  // stripe
  const [clientSecret, setClientSecret] = useState("");

  useEffect(() => {
    if (!data || data.length === 0) {
      console.warn("No data passed to checkout page!");
      return;
    }

    // Calculate Subtotal
    const newSubtotal = data.reduce(
      (acc: any, item: any) => acc + item?.contentData?.price * item.quantity,
      0
    );

    // Calculate Discount
    const newDiscount = data.reduce((acc: any, item: any) => {
      if (item.isPromoApplied && item.afterCoupon < item?.contentData?.price) {
        return (
          acc + (item?.contentData?.price - item.afterCoupon) * item.quantity
        );
      }
      return acc;
    }, 0);

    // Calculate Tax
    const taxRate = 0.1; // Assuming 10% tax rate
    const adjustedSubtotal = newSubtotal - newDiscount;
    const newTax = adjustedSubtotal * taxRate;

    // Calculate Total Due
    const newTotalDue = adjustedSubtotal + newTax;

    setSubtotal(newSubtotal);
    setDiscount(newDiscount);
    setTax(newTax);
    setTotalDue(newTotalDue);
  }, [data]);

  useEffect(() => {
    const fetchSavedCards = async () => {
      if (!currentUser?.uid) return;
      try {
        const savedCards = await PaymentMethodService.getPaymentMethodsByUser(
          currentUser.uid
        );
        setCards(savedCards);
      } catch (error) {
        console.error("Error fetching saved cards:", error);
      }
    };

    fetchSavedCards();
  }, [currentUser]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handlePromoCodeChange = (id: string, value: string) => {
    const updatedData = data.map((item: any) =>
      item.id === id ? { ...item, promoCode: value, error: "" } : item
    );
    navigate("/checkout", { state: { data: updatedData } });
  };

  const applyPromoCode = (id: string) => {
    const updatedData = data.map((item: any) => {
      if (item.id === id) {
        const promo = item.contentData?.promotion; // Fetch promo details
        const isPromoValid = promo?.isActive && promo?.code === item.promoCode;

        if (isPromoValid) {
          const discountPercent = promo.discountPercent || 0;
          const discountAmount =
            (item?.contentData?.price * discountPercent) / 100;

          return {
            ...item,
            afterCoupon: item?.contentData?.price - discountAmount,
            isPromoApplied: true,
            error: "",
          };
        } else {
          return {
            ...item,
            isPromoApplied: false,
            error: "Invalid or inactive promo code",
          };
        }
      }
      return item;
    });

    navigate("/checkout", { state: { data: updatedData } });
  };

  const handleQuantityChange = async (id: string, delta: number) => {
    setLoading(true);

    const updatedData = data.map((item: { id: string; quantity: number }) =>
      item.id === id
        ? { ...item, quantity: Math.max(1, item.quantity + delta) }
        : item
    );
    setSubtotal(
      updatedData.reduce(
        (acc: number, item: { afterCoupon: number; quantity: number }) =>
          acc + item.afterCoupon * item.quantity,
        0
      )
    );

    const updatedItem = data.find((item: any) => item.id === id);
    if (updatedItem) {
      const newQuantity = Math.max(1, updatedItem.quantity + delta);
      try {
        await handleUpdateCartItem(id, newQuantity);
      } catch (error) {
        console.error("Error updating quantity in Firestore:", error);
      } finally {
        setLoading(false);
      }
    }
    navigate("/checkout", { state: { data: updatedData } });
  };

  // Reset unused promo codes
  const removePromoCode = (id: string) => {
    const updatedData = data.map((item: any) =>
      item.id === id
        ? {
          ...item,
          promoCode: "",
          isPromoApplied: false,
          afterCoupon: item?.contentData?.price,
          error: "",
        }
        : item
    );
    navigate("/checkout", { state: { data: updatedData } });
  };

  // Delete item logic
  const handleDelete = async (id: string) => {
    try {
      const isConfirmed = window.confirm(
        "Are you sure you want to delete this item?"
      );
      if (!isConfirmed) {
        return;
      }

      // Remove item locally
      const updatedData = data.filter((item: any) => item.id !== id);
      navigate("/checkout", { state: { data: updatedData } });

      // Remove item from the server
      await CartService.deleteCart(id.toString());
    } catch (error) {
      console.error("Error deleting item:", error);
    }
  };

  const handleNavigateCart = () => {
    navigate("/cart");
  };

  const handleCheckoutPayment = async () => {
    if (!stripe || !elements) {
      alert("Stripe has not loaded yet. Please try again.");
      return;
    }
    setLoading(true);

    try {
      const cardNumberElement = elements.getElement(CardNumberElement);
      const cardExpiryElement = elements.getElement(CardExpiryElement);
      const cardCvcElement = elements.getElement(CardCvcElement);

      if (!cardNumberElement || !cardExpiryElement || !cardCvcElement) {
        alert("Please fill in all card details.");
        setLoading(false);
        return;
      }

      // Get card details from elements
      const { token, error } = await stripe.createToken(cardNumberElement);

      if (error) {
        alert(error.message);
        return;
      }
      console.log("token", token);
      if (token) {
        // Update the card details state
        setCardNumber(token.card?.last4 || "");
        const expMonth =
          token.card?.exp_month?.toString().padStart(2, "0") || "";
        const expYear = token.card?.exp_year?.toString().slice(-2) || "";
        setExpirationDate(dayjs(`${expMonth}/01/${expYear}`));
      }

      if (!countrySelected) {
        alert("Please select a country before proceeding.");
        return;
      }
      // Prepare the payment method data
      const paymentData: PaymentMethodsTypes = {
        id: "", // Will be assigned by Firestore
        userId: currentUser?.uid || "",
        cardName: "", // Example card name, update as needed
        accountNumber: cardNumber,
        cvcNumber: cvc,
        cardNickName: "", // Example nickname, update as needed
        country: countrySelected,
        dateExpiration: expirationDate?.toDate()
          ? Timestamp.fromDate(expirationDate.toDate())
          : undefined,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      // Save card details to Firestore if user opts to save
      if (isSaveCardChecked) {
        try {
          const createdCardRef =
            await PaymentMethodService.createPaymentMethod(paymentData);
          console.log("Card details saved successfully.");
          setCards((prevCards) => [
            ...prevCards,
            { ...paymentData, id: createdCardRef.id },
          ]);
        } catch (error) {
          console.error("Error saving card details:", error);
          alert("Failed to save card details.");
          return;
        }
      }

      // 1. Fetch the user's cart
      const cartData = (await CartService.getCartByUser(
        currentUser.uid
      )) as Cart[];

      if (cartData.length === 0) {
        alert("Cart not found.");
        return;
      }
      console.log("cartData", cartData);

      // Optionally update the cards state to reflect the added card
      // setCards((prevCards) => [
      //   ...prevCards,
      //   { ...paymentData, id: createdCardRef.id },
      // ]);
      setShowModal(true);

      // Optionally update the cards state to reflect the added card
      // setCards((prevCards) => [
      //   ...prevCards,
      //   { ...paymentData, id: createdCardRef.id },
      // ]);
      setShowModal(true);

      // Reset the fields
      // setCardNumber("");
      // setExpirationDate(dayjs());
      // setCvc("");
      // setCountrySelected("");
      // setIsSaveCardChecked(false);

      console.log(">> Updated Cards List:", [...cards, paymentData]);
      // const paymentMethod = await PaymentMethodService.createPaymentMethod(
      //   paymentData
      // );

      const pendingCart = cartData.find((c) => c.status === "pending");
      const cartId = pendingCart?.id;

      // const cartId = pendingCart?.id || cartData[0]?.id;
      if (!cartId) {
        console.warn("No pending cart found.");
        return;
      }

      // 2. Fetch all cart items related to this cart
      const cartItemsResponse = await CartService.getCartItems(currentUser.uid);

      if (cartItemsResponse.items.length === 0) {
        alert("No items found in your cart.");
        return;
      }

      const cartItems = cartItemsResponse.items;
      console.log("Cart Items:", cartItems);

      // 🔥 Stripe Payment Start

      // Call Firebase Cloud Function for Stripe Payment Intent
      const responseStripe = await createPaymentIntent({
        amount: Math.round(totalDue * 100), // Convert to cents
        currency: "usd",
      });
      console.log("responseStripe", responseStripe);

      if (!responseStripe || !responseStripe.clientSecret) {
        throw new Error("Client secret is missing in the response.");
      }

      // // Corrected: Ensure responseStripe is correctly used
      // const { clientSecret } = responseStripe.data as { clientSecret: string };
      const { clientSecret } = responseStripe;

      // Step 2: Confirm Payment with Stripe
      if (!cardNumberElement) {
        alert("Card details are missing.");
        return;
      }

      const { error: paymentError, paymentIntent } =
        await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: cardNumberElement,
          },
        });

      if (paymentError) {
        console.error("Payment failed:", paymentError);
        alert("Payment failed. Please try again.");
        return;
      }

      console.log("Payment successful with stripe:", paymentIntent);

      // 🔥 Stripe Payment End

      setCardNumber("");
      setExpirationDate(dayjs());
      setCvc("");
      setCountrySelected("");
      setIsSaveCardChecked(false);

      // 3. Update cart with subtotal and tax
      await CartService.updateCart(cartId, {
        subTotal: subtotal,
        tax: tax,
      });

      // 4. Prepare purchase data for all items in the cart
      const purchaseData: any = {
        userId: currentUser.uid,
        cartId: cartId,
        contentId: cartItems.map((item: any) => item?.contentData?.id),
        transactionId: `txn_${Date.now()}`,
        amount: subtotal,
        tax: tax,
        currentStatus: "PENDING",
        statusHistory: [{ status: "PENDING", date: new Date() }],
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      console.log("Mapped purchaseData", purchaseData);

      // 5. Save the purchase document to Firestore
      const response = await PurchasesService.createPurchase(purchaseData);

      console.log("Purchases successfully created.", response);

      // Update the cart status to completed
      await CartService.updateCart(cartId, { status: "completed" });
      console.log(`Cart status updated to 'completed' for cart ID: ${cartId}`);

      alert("Payment completed successfully!");

      // Redirect after successful payment
      navigate("/");
    } catch (error) {
      console.error("Error during payment processing", error);
      alert("An error occurred while processing your payment.");
    } finally {
      setLoading(false);
    }
  };

  // Handle card selection
  const handleSelectCard = (card: PaymentMethodsTypes) => {
    setCardNumber(card.accountNumber);
    setExpirationDate(dayjs(card?.dateExpiration?.toDate()));
    setCvc("");
    setCountrySelected(card.country || "");
    setShowModal(true);
  };


  return (
    <Grid position={"relative"}>
      <Grid sx={styleSheet.container}>
        <Box
          pt={{ xs: 10, md: 3 }}
          fontWeight={700}
          fontSize="35px"
          color="#233F91"
        >
          Checkout
        </Box>
        <Box onClick={handleNavigateCart} sx={styleSheet.navigateCartBtn}>
          <KeyboardArrowLeftIcon sx={{ color: "#233F91", fontSize: "18px" }} />
          Back to cart
        </Box>
        <Box
          pt={1}
          fontWeight={500}
          fontSize={{ xs: "18px", md: "28px" }}
          color="#233F91"
        >
          1. Content Information & Review
        </Box>
        <Box>
          <CheckOutCommon
            data={data}
            onQuantityChange={handleQuantityChange}
            onDelete={handleDelete}
            removePromoCode={removePromoCode}
            applyPromoCode={applyPromoCode}
            handlePromoCodeChange={handlePromoCodeChange}
            titleColor="#233F91"
          />
        </Box>

        <Box
          display={"flex"}
          flexDirection={"column"}
          gap={2}
          mt={5}
          width={{ xs: "100%", md: "800px" }}
        >
          <Box display={"flex"} justifyContent={"space-between"}>
            <Typography sx={styleSheet.textHeading}>Subtotal:</Typography>
            <Typography sx={styleSheet.textHeadingPrice}>
              ${subtotal.toFixed(2)}
            </Typography>
          </Box>
          <Box display={"flex"} justifyContent={"space-between"}>
            <Typography sx={styleSheet.textHeading}>Promo Code</Typography>
            <Typography
              sx={{ ...styleSheet.textHeadingPrice, color: "#1DA944" }}
            >
              -${discount.toFixed(2)}
            </Typography>
          </Box>
          <Box display={"flex"} justifyContent={"space-between"}>
            <Typography sx={styleSheet.textHeading}>Tax</Typography>
            <Typography sx={styleSheet.textHeadingPrice}>
              ${tax.toFixed(2)}
            </Typography>
          </Box>
          <Box display={"flex"} sx={{ borderTop: "1px solid #00A79D" }}></Box>
          <Box display={"flex"} justifyContent={"space-between"} mt={2}>
            <Typography sx={styleSheet.textHeading}>Total Due</Typography>
            <Typography sx={styleSheet.textHeadingPrice}>
              ${totalDue.toFixed(2)}
            </Typography>
          </Box>
        </Box>

        {/* /// credit card stripe info /// */}
        <Box
          pt={5}
          fontWeight={500}
          fontSize="28px"
          color="#233F91"
          display="flex"
          alignItems="center"
        >
          2. Payment Details
        </Box>

        <Box sx={styleSheet.criditInfoContainer}>
          {/* Add New Card Modal */}
          {/* {showModal && (
            <AddNewCardModal
              cards={cards}
              open={showModal}
              onClose={() => setShowModal(false)}
              onSelectCard={handleSelectCard}
            />
          )} */}

          <Box sx={styleSheet.creditIcon}>
            <CreditCardIcon sx={{ color: "#0570DE" }} />
            <Typography sx={{ ...styleSheet.fieldLabel, color: "#0570DE" }}>
              Card
            </Typography>
          </Box>
          <Box
            display={"flex"}
            flexDirection={"column"}
            width={{ xs: "100%", md: "531.24px" }}
          >
            <Box display={"flex"} flexDirection={"column"}>
              <Typography sx={styleSheet.fieldLabel}>Card number</Typography>
              <Box sx={styleSheet.stripeFieldContainer}>
                <CardNumberElement
                  options={CARD_NUMBER_ELEMENT_OPTIONS}
                  onChange={(e) => {
                    if (e.error) {
                      console.log("[error]", e.error);
                    }
                  }}
                />
              </Box>
            </Box>

            <Box display={"flex"} gap={1}>
              <Box display={"flex"} flexDirection={"column"} flex={1}>
                <Typography sx={styleSheet.fieldLabel}>Expiry</Typography>
                <Box sx={styleSheet.stripeFieldContainer}>
                  <CardExpiryElement
                    options={CARD_EXPIRY_ELEMENT_OPTIONS}
                    onChange={(e) => {
                      if (e.error) {
                        console.log("[error]", e.error);
                      }
                    }}
                  />
                </Box>
              </Box>

              <Box display={"flex"} flexDirection={"column"} flex={1}>
                <Typography sx={styleSheet.fieldLabel}>CVC</Typography>
                <Box sx={styleSheet.stripeFieldContainer}>
                  <CardCvcElement
                    options={CARD_CVC_ELEMENT_OPTIONS}
                    onChange={(e) => {
                      if (e.error) {
                        console.log("[error]", e.error);
                      }
                    }}
                  />
                </Box>
              </Box>
            </Box>

            <Box></Box>
            <Box display={"flex"} flexDirection={"column"}>
              <Typography sx={styleSheet.fieldLabel}>Country</Typography>

              <TextField
                placeholder="Select"
                fullWidth
                value={countrySelected}
                onChange={(e) => setCountrySelected(e.target.value)}
                select
                InputLabelProps={{
                  shrink: true,
                }}
                sx={styleSheet.textField}
              >
                <MenuItem value="United States">United States</MenuItem>
                <MenuItem value="Canada">Canada</MenuItem>
              </TextField>
            </Box>
            {/* <Box
              display={"flex"}
              alignItems={"center"}
              justifyContent={"space-between"}
              mr={0}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isSaveCardChecked}
                    onChange={(e) => setIsSaveCardChecked(e.target.checked)}
                    sx={{
                      color: "#0A46E4",
                      "&.Mui-checked": {
                        color: "#233F91",
                        backgroundColor: "#EDEDED",
                      },
                      "& .MuiFormControlLabel-root": {
                        margingRight: 0,
                      },
                    }}
                  />
                }
                label="Save this card for future Affekti payments"
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: { xs: "12px", md: "17px" },
                    fontWeight: 400,
                    color: "#233F91",
                  },
                  marginRight: 0,
                }}
              />
            </Box> */}
          </Box>
        </Box>
        <Button
          sx={styleSheet.completePaymentBtn}
          onClick={handleCheckoutPayment}
          // disabled={!isSaveCardChecked}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={24} sx={{ color: "white" }} />
          ) : (
            "Complete Payment"
          )}
        </Button>
      </Grid>
    </Grid>
  );
};

export default CheckOutData;

const styleSheet = {
  textField: {
    mb: 2,
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderRadius: "15px",
        border: "2.39px solid #E0E0E0",
        boxShadow: "0px 0px 0px 0px #E0E0E0",
      },
      height: "53px",
    },
  },

  completePaymentBtn: {
    width: { xs: "100%", md: "849px" },
    height: "57px",
    borderRadius: "15px",
    bgcolor: "#233F91",
    fontSize: "18px",
    fontWeight: "700",
    color: "#fff",
    marginTop: "15px",
    "&:hover": {
      bgcolor: "#1A73E8",
    },
  },
  navigateCartBtn: {
    pt: { xs: 0, md: 1 },
    fontWeight: 400,
    fontSize: "18px",
    color: "#233F91",
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
  },
  textHeading: {
    fontWeight: 400,
    fontSize: "20px",
    color: "#313131",
    lineHeight: "24px",
  },
  textHeadingPrice: {
    fontWeight: 700,
    fontSize: "18px",
    color: "#000000",
    lineHeight: "21.6px",
  },
  fieldLabel: {
    fontSize: "18px",
    fontWeight: "700",
    color: "#595959",
  },
  criditInfoContainer: {
    borderRadius: "15px",
    display: "flex",
    boxShadow: "0px 1px 1px 1px rgba(0, 0, 0, 0.25)",
    p: 3,
    flexDirection: "column",
    border: "1px solid #F2F2F2",
    gap: 2,
    width: { xs: "85%", md: "800px" },
    mt: 2,
  },
  container: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    p: 5,
    px: { xs: 2, md: 10 },
  },
  creditIcon: {
    border: "2.39px solid #0570DE",
    borderRadius: "15px",
    p: 1,
    width: "119.66px",
  },
  formControlLabel: {
    "& .MuiFormControlLabel-label": {
      fontSize: { xs: "12px", md: "17px" },

      fontWeight: 400,
      color: "#233F91",
    },
    "& .MuiFormControlLabel-root": {
      margingRight: 0,
    },
  },
  datePickerStyle: {
    width: { xs: "100%", md: "259px" },
    "& .MuiInputBase-root": {
      backgroundColor: "#fff",
      borderRadius: "15px",
      height: "50px",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "2.39px solid #E0E0E0",
      boxShadow: "0px 0px 0px 0px #E0E0E0", // Regular border color
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: "2.39px solid #E0E0E0",
      boxShadow: "0px 0px 0px 0px #E0E0E0",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: "2.39px solid #E0E0E0",
      boxShadow: "0px 0px 0px 4px #C9C9C9",
    },
    "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
      border: "2.39px solid #E0E0E0",
      boxShadow: "0px 0px 0px 0px #E0E0E0", // Override the red border color when in error state
    },
    "& .MuiSvgIcon-root": {
      color: "#707070",
    },
  },
  stripeFieldContainer: {
    position: "relative",
    border: "2.39px solid #E0E0E0",
    borderRadius: "15px",
    minHeight: "53px",
    backgroundColor: "#fff",
    marginBottom: "16px",
    transition: "all 0.2s ease",
    cursor: "text",

    "& .StripeElement": {
      width: "100%",
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      padding: "15px",
      "&--focus": {
        outline: "none",
      },
      "&--invalid": {
        borderColor: "#9e2146",
      },
    },

    "&:hover": {
      borderColor: "#1976d2",
    },

    "&:focus-within": {
      borderColor: "#1976d2",
      boxShadow: "0px 0px 0px 4px rgba(25, 118, 210, 0.1)",
    },
  },
};
